/**
 * Whether left and right array items are `===`.
 *
 * @param left
 * @param right
 */
export function shallowItemsAreEqual<A extends readonly unknown[]>(
  left: A,
  right: A,
) {
  if (left === right) {
    return true
  }

  if (left.length !== right.length) {
    return false
  }

  if (left.length === 0 && right.length === 0) {
    return true
  }

  return left.every((lv, i) => right[i] === lv)
}

/**
 * Removes the first occurrence of the given element from the array in place.
 *
 * @param array The array to remove the element from.
 * @param element The element to remove.
 *
 * @returns `true` if the element was removed, `false` otherwise.
 */
export function removeElement<T>(array: T[], element: T): boolean {
  const index = array.indexOf(element)
  if (index !== -1) {
    array.splice(index, 1)
    return true
  }

  return false
}

interface BinarySearchOptions<T> {
  /**
   * Custom comparator. Must return a negative non-zero number
   * when `a` is less than `b`, a positive non-zero number when
   * `a` is greater than `b`, and `0` when they are equal.
   *
   * @param a
   * @param b
   */
  comparator?: (a: T, b: T) => number
  /**
   * If `true`, returns the index of the closest match, preferring the higher
   * value.
   */
  closest?: boolean
}

interface BinarySearchOptionsWithSelector<T, V>
  extends Omit<BinarySearchOptions<T>, 'comparator'> {
  /**
   * Custom comparator. Must return a negative non-zero number
   * when `a` is less than `b`, a positive non-zero number when
   * `a` is greater than `b`, and `0` when they are equal.
   *
   * @param a
   * @param b
   */
  comparator?: (a: V, b: V) => number
  /**
   * Custom selector. Must return a value that can be used to compare
   * the item with the search value.
   *
   * @param item
   */
  selector: (item: T) => V
}

/**
 * Binary search on an array.
 */
export function binarySearch<T>(
  array: T[],
  value: T,
  options?: BinarySearchOptions<T>,
): number
export function binarySearch<T, V>(
  array: T[],
  value: V,
  options: BinarySearchOptionsWithSelector<T, V>,
): number
export function binarySearch<T, V>(
  array: T[],
  value: V,
  options?: BinarySearchOptions<T> | BinarySearchOptionsWithSelector<T, V>,
): number {
  let selector: ((item: T) => V) | undefined
  options ??= {}
  const { comparator, closest } = options ?? {}
  if ('selector' in options) {
    selector = options.selector
  }

  let low = 0
  let high = array.length - 1
  let mid = -1
  let lastCheckedIndex = -1

  while (low <= high) {
    mid = (low + high) >>> 1
    const midItem = array[mid]
    const midValue = selector ? selector(midItem) : midItem

    const cmp = comparator
      ? // @ts-ignore
        comparator(midValue, value)
      : Number(midValue) - Number(value)
    if (cmp < 0) {
      low = mid + 1
    } else if (cmp > 0) {
      high = mid - 1
    } else {
      return mid
    }
    lastCheckedIndex = mid
  }

  if (!closest) {
    return -1
  }

  if (low === 0) {
    return 0
  }

  if (high === array.length - 1) {
    return array.length - 1
  }

  return lastCheckedIndex
}
